import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import type { AtsType, CompanyCategory, Market, MarketStats } from '@/api/types'
import { Button, buttonClasses } from '@/components/ui/Button'
import { EmptyState, ErrorBox, PageHeader, RunStatusBadge } from '@/components/ui/Primitives'
import { errorMessage, toast } from '@/components/ui/Toast'
import { CompanyFormModal } from '@/features/companies/CompanyFormModal'
import { ImportCompaniesModal } from '@/features/companies/ImportCompaniesModal'
import { HiringInsights } from '@/features/overview/HiringInsights'
import {
  ATS_LABEL,
  CATEGORIES,
  CATEGORY_LABEL,
  MARKET_FLAG,
  MARKET_LABEL,
  formatNumber,
  relativeTime,
} from '@/lib/utils'

export function OverviewPage() {
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const stats = useQuery({ queryKey: ['stats'], queryFn: api.stats, refetchInterval: 10_000 })
  const recentNew = useQuery({
    queryKey: ['runs', 'new'],
    queryFn: () => api.runs({ new_only: true, page_size: 8 }),
    refetchInterval: 15_000,
  })
  const s = stats.data
  const atsEntries = Object.entries(s?.career_pages_by_ats ?? {}) as Array<[AtsType, number]>
  const atsMax = Math.max(1, ...atsEntries.map(([, n]) => n))

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle="Companies in India and the USA, checked on a schedule for new openings."
        actions={
          <>
            <Button variant="secondary" onClick={() => setImporting(true)}>
              Import CSV
            </Button>
            <Button onClick={() => setAdding(true)}>Add company</Button>
          </>
        }
      />
      {stats.isError && <ErrorBox error={stats.error} onRetry={() => void stats.refetch()} />}

      {s && s.companies_total === 0 && (
        <div className="card">
          <EmptyState
            title="No companies yet"
            body="Start with the company list: add companies one by one or import your MNC / GCC list. The scheduler then checks each company's career pages."
            action={
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setImporting(true)}>
                  Import CSV
                </Button>
                <Button onClick={() => setAdding(true)}>Add company</Button>
              </div>
            }
          />
        </div>
      )}

      {s && s.companies_total > 0 && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {(['IN', 'US'] as Market[]).map((m) => (
              <MarketCard key={m} market={m} stats={s.markets[m]} />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
            <Stat label="Companies" value={formatNumber(s.companies_total)} to="/companies" />
            <Stat label="Open jobs" value={formatNumber(s.jobs_active)} to="/jobs" />
            <Stat label="New openings · 24h" value={formatNumber(s.jobs_new_24h)} tone="success" to="/jobs?new_only=true&found_within_days=1&sort=recent" />
            <Stat label="Closed · 24h" value={formatNumber(s.jobs_closed_24h)} to="/jobs?active=false" />
            <Stat
              label="Checks · 24h"
              value={formatNumber((s.runs_24h.success ?? 0) + (s.runs_24h.empty ?? 0) + (s.runs_24h.failed ?? 0))}
              hint={s.runs_24h.failed ? `${s.runs_24h.failed} failed` : undefined}
              to="/scheduler"
            />
            <Stat label="Checking now" value={`${s.crawling_now} / ${s.scheduler_concurrency}`} hint={s.queue_due ? `${s.queue_due} due` : undefined} to="/scheduler" />
          </div>

          <HiringInsights />

          <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <section className="card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4">
                <h2 className="text-[15px] font-semibold">Latest checks that found new openings</h2>
                <Link to="/scheduler?show=new" className="link text-[13px]">
                  All
                </Link>
              </div>
              <ul>
                {recentNew.data?.items.map((run) => (
                  <li key={run.id} className="flex items-center gap-3 border-t border-line px-5 py-2.5 text-[13.5px]">
                    <RunStatusBadge status={run.status} />
                    <Link to={`/companies/${run.company_id}`} className="min-w-0 flex-1 truncate font-medium hover:underline">
                      {run.company_name}
                    </Link>
                    <Link
                      to={`/jobs?company_id=${run.company_id}&new_only=true&found_within_days=7&sort=recent`}
                      className="shrink-0 font-medium text-[#1f7a4d] hover:underline"
                    >
                      +{formatNumber(run.jobs_new)} new
                    </Link>
                    <span className="w-24 shrink-0 text-right text-[12.5px] text-ink-faint">{relativeTime(run.started_at)}</span>
                  </li>
                ))}
                {recentNew.data?.items.length === 0 && (
                  <li className="border-t border-line px-5 py-6 text-[13.5px] text-ink-muted">
                    No new openings detected yet. The first check of each company records what is already open; openings posted after that appear here.
                  </li>
                )}
              </ul>
            </section>

            <section className="card p-5">
              <h2 className="mb-4 text-[15px] font-semibold">Career pages by platform</h2>
              <ul className="space-y-2.5">
                {atsEntries.map(([ats, count]) => (
                  <li key={ats}>
                    <div className="mb-1 flex justify-between text-[13px]">
                      <span>{ATS_LABEL[ats]}</span>
                      <span className="tabular-nums text-ink-muted">{formatNumber(count)}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
                      <div className="h-full rounded-full bg-ink/70" style={{ width: `${(count / atsMax) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}

      {adding && <CompanyFormModal onClose={() => setAdding(false)} />}
      {importing && <ImportCompaniesModal onClose={() => setImporting(false)} />}
    </>
  )
}

function Stat({ label, value, hint, tone, to }: { label: string; value: string; hint?: string; tone?: 'success'; to: string }) {
  return (
    <Link to={to} className="card block px-4 py-3.5 transition hover:border-line-strong">
      <p className="label-eyebrow">{label}</p>
      <p className={`mt-1.5 text-[22px] font-semibold tabular-nums ${tone === 'success' ? 'text-[#1f7a4d]' : ''}`}>{value}</p>
      {hint && <p className="text-[12px] text-ink-muted">{hint}</p>}
    </Link>
  )
}

function MarketCard({ market, stats }: { market: Market; stats: MarketStats }) {
  const qc = useQueryClient()
  const check = useMutation({
    mutationFn: () => api.checkMany({ market }),
    onSuccess: (r) => {
      toast.success(`${formatNumber(r.companies)} ${MARKET_LABEL[market]} companies queued`)
      void qc.invalidateQueries({ queryKey: ['stats'] })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const tiers = ['L1', 'L2', 'L3', 'unrated'].filter((t) => stats.by_tier[t])
  return (
    <section className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[17px] font-semibold">
          {MARKET_FLAG[market]} {MARKET_LABEL[market]}
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" loading={check.isPending} disabled={!stats.companies} onClick={() => check.mutate()}>
            Check all now
          </Button>
          <Link to={`/jobs?market=${market}`} className={buttonClasses('primary', 'sm')}>
            View jobs
          </Link>
        </div>
      </div>
      <dl className="mb-4 grid grid-cols-3 gap-4">
        <div>
          <dt className="label-eyebrow">Open jobs</dt>
          <dd className="mt-1 text-[26px] font-semibold tabular-nums">{formatNumber(stats.jobs_active)}</dd>
        </div>
        <div>
          <dt className="label-eyebrow">New · 7 days</dt>
          <dd className="mt-1 text-[26px] font-semibold tabular-nums text-[#1f7a4d]">
            <Link to={`/jobs?market=${market}&new_only=true&found_within_days=7&sort=recent`} className="hover:underline">
              +{formatNumber(stats.jobs_new_7d)}
            </Link>
          </dd>
        </div>
        <div>
          <dt className="label-eyebrow">Companies</dt>
          <dd className="mt-1 text-[26px] font-semibold tabular-nums">
            <Link to={`/companies?market=${market}`} className="hover:underline">
              {formatNumber(stats.companies)}
            </Link>
          </dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-1.5 border-t border-line pt-3 text-[12.5px]">
        {CATEGORIES.filter((c) => stats.by_category[c]).map((c: CompanyCategory) => (
          <Link key={c} to={`/companies?market=${market}&category=${c}`} className="rounded-pill border border-line px-2.5 py-1 hover:border-line-strong">
            {CATEGORY_LABEL[c]} <b className="font-semibold">{formatNumber(stats.by_category[c])}</b>
          </Link>
        ))}
        <span className="mx-1 self-center text-ink-faint">·</span>
        {tiers.map((t) => (
          <Link
            key={t}
            to={t === 'unrated' ? `/companies?market=${market}` : `/companies?market=${market}&tier=${t.slice(1)}`}
            className="rounded-pill border border-line px-2.5 py-1 hover:border-line-strong"
          >
            {t === 'unrated' ? 'No level' : `Level ${t.slice(1)}`} <b className="font-semibold">{formatNumber(stats.by_tier[t])}</b>
          </Link>
        ))}
      </div>
    </section>
  )
}
